import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { Capacitor } from "@capacitor/core";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { EmptyStateIcon } from "../../../commons/EmptyStateIcon.js";
import { CREATE_TEMPLATE_URL_PARAM } from "./buttons/createTemplateUrlParam.js";

/**
 * Design D9's zero-result states — replaces the results area (list, gallery
 * and table views render the same panel) rather than showing a blank list.
 * See `zeroResultState.ts` for the branch rule that decides which of these
 * (if either) to render. Both panels share the same icon/headline/subtitle/
 * action layout, centered — the "quote filters" chip lives in the filter
 * row now, not here (see TemplatesQuoteFiltersChip.tsx).
 */

export function ZeroResultFiltersHideMatchesPanel({
  relaxedCount,
  searchTargetName,
  onClearFilters,
  onBuildFromProducts,
  quoteFilterCount,
}: {
  relaxedCount: number;
  /**
   * The quoted name in the copy — the handoff's mockups always quote what
   * the user is actually searching *for*, not the placeholder's type: the
   * current search term when one is typed, falling back to the template
   * type's name only once the search box is empty (see the call site in
   * `TemplatesQuoteSelectMultipleModal.tsx`).
   */
  searchTargetName: string;
  onClearFilters: () => void;
  onBuildFromProducts?: () => void;
  /**
   * Set only when the quote's own seeded filters are the *entire* reason
   * for the zero result (see `isPureQuoteFiltersCause` in
   * `zeroResultState.ts`) — lets the copy name "your quote's filters"
   * specifically and truthfully. Omit whenever a filter outside the seed
   * (category, project type, a changed template type, price, tags,
   * created-by/date, or another property) is also active — naming the
   * quote alone in that case would be a half-truth, since clearing only
   * the quote's filters wouldn't necessarily reveal anything.
   */
  quoteFilterCount?: number;
}) {
  const { t } = useTranslate(["TemplatesSearch"]);
  const isPureQuoteCause = !!quoteFilterCount;

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        spacing={1.5}
        sx={{ maxWidth: 560, textAlign: "center", alignItems: "center" }}
      >
        <EmptyStateIcon size={48} borderWidth={2} />
        <Typography variant="h3">
          {t("0 templates with your filters — {relaxedCount} without", {
            ns: "TemplatesSearch",
            relaxedCount,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isPureQuoteCause
            ? t(
                'The {filterCount} filters carried over from this quote are hiding all matches for "{templateTypeName}".',
                {
                  ns: "TemplatesSearch",
                  filterCount: quoteFilterCount,
                  templateTypeName: searchTargetName,
                }
              )
            : t(
                'Your filters are hiding all matches for "{templateTypeName}".',
                { ns: "TemplatesSearch", templateTypeName: searchTargetName }
              )}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "nowrap",
            whiteSpace: "nowrap",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onClearFilters}
            sx={{ textTransform: "none" }}
          >
            {isPureQuoteCause
              ? t("Clear quote filters · show {relaxedCount} templates", {
                  ns: "TemplatesSearch",
                  relaxedCount,
                })
              : t("Clear filters · show {relaxedCount} templates", {
                  ns: "TemplatesSearch",
                  relaxedCount,
                })}
          </Button>
          {onBuildFromProducts && (
            <Link
              component="button"
              variant="body2"
              onClick={onBuildFromProducts}
            >
              {t("or build it from products", { ns: "TemplatesSearch" })}
            </Link>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export function ZeroResultNothingExistsPanel({
  searchTargetName,
  onBuildFromProducts,
}: {
  /** See `ZeroResultFiltersHideMatchesPanel`'s doc — same quoting rule. */
  searchTargetName: string;
  onBuildFromProducts?: () => void;
}) {
  const { t } = useTranslate(["TemplatesSearch"]);
  const isNative = Capacitor.isNativePlatform();

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        spacing={1.5}
        sx={{ maxWidth: 480, textAlign: "center", alignItems: "center" }}
      >
        <EmptyStateIcon size={48} borderWidth={2} />
        <Typography variant="h3">
          {t('No template for "{templateTypeName}" yet', {
            ns: "TemplatesSearch",
            templateTypeName: searchTargetName,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            "Nothing matches — with or without your quote filters. Build the section from products now; you can save it as a template afterwards.",
            { ns: "TemplatesSearch" }
          )}
        </Typography>
        {onBuildFromProducts && (
          <Button
            variant="contained"
            color="primary"
            onClick={onBuildFromProducts}
            sx={{ textTransform: "none" }}
          >
            {t("Build it from products", { ns: "TemplatesSearch" })}
          </Button>
        )}
        {/* Internal app routes, not external URLs — a webview window.open
         *  either fails silently or escapes the app, so these are web-only. */}
        {!isNative && (
          <Stack
            direction="row"
            spacing={2.5}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <RestrictedByOrganisationPermissionWithDebug permission="template_share_create">
              <Link
                component="button"
                variant="body2"
                onClick={() =>
                  window.open("/templates/documents/store", "_blank")
                }
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {t("Browse the Meister library", { ns: "TemplatesSearch" })}
                <OpenInNewIcon fontSize="inherit" />
              </Link>
            </RestrictedByOrganisationPermissionWithDebug>
            <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
              <Link
                component="button"
                variant="body2"
                onClick={() =>
                  window.open(
                    `/templates/documents/my?${CREATE_TEMPLATE_URL_PARAM}`,
                    "_blank"
                  )
                }
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {t("Create a new template", { ns: "TemplatesSearch" })}
                <OpenInNewIcon fontSize="inherit" />
              </Link>
            </RestrictedByOrganisationPermissionWithDebug>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
